/**
 * A3（Qwen-image）分辨率映射 —— 唯一来源对齐对接文档 §2.2。
 * 禁止另造一套像素表。
 */
export interface A3ResolutionOption {
  /** 产品比例标识，对应 aspectRatios */
  aspectRatios: string;
  width: number;
  height: number;
  /** UI 展示用 */
  label: string;
}

/** 对接文档 §2.2 全表（含 1:1 三档） */
export const A3_RESOLUTION_OPTIONS: A3ResolutionOption[] = [
  { aspectRatios: '1:1', width: 512, height: 512, label: '512x512' },
  { aspectRatios: '1:1', width: 1024, height: 1024, label: '1024x1024' },
  { aspectRatios: '1:1', width: 1328, height: 1328, label: '1328x1328' },
  { aspectRatios: '4:3', width: 1216, height: 832, label: '1216x832' },
  { aspectRatios: '3:4', width: 832, height: 1216, label: '832x1216' },
  { aspectRatios: '3:2', width: 1344, height: 768, label: '1344x768' },
  { aspectRatios: '2:3', width: 768, height: 1344, label: '768x1344' },
  { aspectRatios: '16:9', width: 1536, height: 640, label: '1536x640' },
  { aspectRatios: '9:16', width: 640, height: 1536, label: '640x1536' },
  { aspectRatios: '21:9', width: 2016, height: 864, label: '2016x864' },
  { aspectRatios: '9:21', width: 864, height: 2016, label: '864x2016' },
];

/** 默认方图（对接表「默认方图」） */
export const A3_DEFAULT_RESOLUTION = A3_RESOLUTION_OPTIONS[2];

/** A3 画面增强 Steps 默认与范围 */
export const A3_PERTURB_DEFAULT = 20;
export const A3_PERTURB_MIN = 10;
export const A3_PERTURB_MAX = 30;

/** A3 重绘强度（denoise）口径（需求 §5.1/§5.2）：文生固定 1；图生默认 0.8，开启画面增强后 0.7 */
export const A3_DENOISE_T2I = 1;
export const A3_DENOISE_I2I_DEFAULT = 0.8;
export const A3_DENOISE_I2I_ENHANCED = 0.7;

/** A3 LoRA 数量上限（与 Explore / 画布非 A3 一致为 5） */
export const A3_LORA_MAX = 5;

/**
 * 按 aspectRatios（或 label 像素）解析宽高；无法匹配时回落默认方图。
 */
export function resolveA3Size(aspectRatios?: string, width?: number, height?: number): A3ResolutionOption {
  if (width && height) {
    const byPx = A3_RESOLUTION_OPTIONS.find((o) => o.width === width && o.height === height);
    if (byPx) return byPx;
  }
  if (aspectRatios) {
    // 兼容旧 Flux 伪比例 2:2 / 3:3 → 1:1
    const normalized =
      aspectRatios === '2:2' || aspectRatios === '3:3' ? '1:1' : aspectRatios;
    const candidates = A3_RESOLUTION_OPTIONS.filter((o) => o.aspectRatios === normalized);
    if (candidates.length === 1) return candidates[0];
    if (candidates.length > 1) {
      // 多档 1:1：优先默认 1328，否则取列表中最大边
      return candidates.find((o) => o.width === A3_DEFAULT_RESOLUTION.width) ?? candidates[candidates.length - 1];
    }
  }
  return A3_DEFAULT_RESOLUTION;
}

/**
 * 将 ImageSetting 选中的 value 还原为 aspectRatios + 宽高（A3）。
 * value 格式：`${aspectRatios}@${width}x${height}`
 */
export function parseA3AspectSelection(value: string): {
  aspectRatios: string;
  width: number;
  height: number;
} {
  const encoded = /^(.*)@(\d+)x(\d+)$/.exec(value);
  if (encoded) {
    return {
      aspectRatios: encoded[1],
      width: Number(encoded[2]),
      height: Number(encoded[3]),
    };
  }
  const size = resolveA3Size(value);
  return { aspectRatios: size.aspectRatios, width: size.width, height: size.height };
}

/**
 * 历史记录里 aspectRatios 常为纯比例（如 1:1），宽高在 width/height。
 * 编辑复用回填面板时需编码成 ImageSetting 选项 value，才能命中对应像素档。
 */
export function encodeA3AspectForUi(
  aspectRatios?: string | null,
  width?: number | null,
  height?: number | null
): string {
  if (typeof aspectRatios === 'string' && aspectRatios.includes('@')) {
    return aspectRatios;
  }
  const size = resolveA3Size(aspectRatios ?? undefined, width ?? undefined, height ?? undefined);
  return `${size.aspectRatios}@${size.width}x${size.height}`;
}
